import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import {
  SubjectSiteAccount,
  SubjectSiteApi,
  SubjectSiteShell,
} from 'api/subjectSiteApi';
import Private from 'components/private/Private';
import SubjectSiteFooter from './SubjectSiteFooter';
import SubjectSiteHeader from './SubjectSiteHeader';
import SubjectSiteLessonMenu from './SubjectSiteLessonMenu';
import {
  subjectSiteAccountLinks,
  subjectSiteMenuLinks,
  subjectSiteSchoolLinks,
} from './subjectSiteMenu';
import SubjectSiteSideMenu from './SubjectSiteSideMenu';
import SubjectSiteTrialBanner from './SubjectSiteTrialBanner';

type Props = {
  children?: React.ReactNode;
  /**
   * The page's colour theme class, for example Blue_GSFPE or an activity's colour
   */
  colorCss?: string | null;
  /**
   * The subject site shows a free trial's days left on its home page only
   */
  bShowTrialBanner?: boolean;
};

const COVER_LAYER_FADE_MILLISECONDS = 200;

type LoadedHeader = {
  key: string;
  shell: SubjectSiteShell | null;
  account: SubjectSiteAccount | null;
};

/**
 * The last header loaded, shown at once on the next page for the same sign-in
 * and subject while it loads again
 */
let lastHeader: LoadedHeader | null = null;

const headerKey = (subjectCode: string) =>
  `${window.localStorage.getItem('RTE') ?? ''}|${subjectCode}`;

/**
 * The layout of every signed-in page under a subject's path: the subject's own
 * header, side menu, lesson plan menu and footer. A signed-out user goes to sign
 * in; what the page shows is the API's decision.
 */
const SubjectSiteLayout: React.FC<Props> = (props) => (
  <Private>
    <SignedInSubjectSiteLayout {...props} />
  </Private>
);

const SignedInSubjectSiteLayout: React.FC<Props> = ({
  children,
  colorCss,
  bShowTrialBanner = false,
}) => {
  const { pathname } = useLocation();
  const subjectCode = pathname.split('/')[1]?.toLowerCase() ?? '';

  const initialHeader =
    lastHeader?.key === headerKey(subjectCode) ? lastHeader : null;
  const [shell, setShell] = useState<SubjectSiteShell | null>(
    initialHeader?.shell ?? null
  );
  const [account, setAccount] = useState<SubjectSiteAccount | null>(
    initialHeader?.account ?? null
  );
  const [bAccountLoaded, setBAccountLoaded] = useState(!!initialHeader);
  const [bSideMenuOpen, setBSideMenuOpen] = useState(false);
  const [bLessonMenuOpen, setBLessonMenuOpen] = useState(false);
  const [bCoverShown, setBCoverShown] = useState(false);
  const bCoverOpen = bSideMenuOpen || bLessonMenuOpen;

  useEffect(() => {
    let bCurrent = true;
    const key = headerKey(subjectCode);
    if (lastHeader?.key !== key) {
      setShell(null);
      setAccount(null);
      setBAccountLoaded(false);
    }
    // Shown together, so the header's links don't change once on the page
    SubjectSiteApi.getShell(subjectCode)
      .then(async (loadedShell) => ({
        shell: loadedShell,
        account: loadedShell
          ? await SubjectSiteApi.getAccount(loadedShell.subject)
          : null,
      }))
      .catch(() => ({ shell: null, account: null }))
      .then((loaded) => {
        if (!bCurrent) return;
        lastHeader = loaded.shell ? { key, ...loaded } : null;
        setShell(loaded.shell);
        setAccount(loaded.account);
        setBAccountLoaded(true);
      });
    return () => {
      bCurrent = false;
    };
  }, [subjectCode]);

  const closeAll = () => {
    setBSideMenuOpen(false);
    setBLessonMenuOpen(false);
  };

  useEffect(() => {
    if (bCoverOpen) {
      setBCoverShown(true);
      const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') closeAll();
      };
      document.addEventListener('keyup', closeOnEscape);
      return () => document.removeEventListener('keyup', closeOnEscape);
    }
    const timeout = window.setTimeout(
      () => setBCoverShown(false),
      COVER_LAYER_FADE_MILLISECONDS
    );
    return () => window.clearTimeout(timeout);
  }, [bCoverOpen]);

  const menuLinks = shell
    ? subjectSiteMenuLinks(subjectCode, shell, account)
    : [];
  const schoolLinks = subjectSiteSchoolLinks(shell, account);
  const openLessonMenu = shell ? () => setBLessonMenuOpen(true) : null;
  const bTrialBanner =
    bShowTrialBanner &&
    !!account?.bFreeTrial &&
    !!account.subscriptionRenewalDate &&
    new Date() < new Date(account.subscriptionRenewalDate);

  return (
    <div className={`SubjectSite${colorCss ? ` ${colorCss}` : ''}`}>
      {bTrialBanner && account?.subscriptionRenewalDate && (
        <SubjectSiteTrialBanner
          subscriptionRenewalDate={account.subscriptionRenewalDate}
        />
      )}
      <SubjectSiteHeader
        menuLinks={menuLinks}
        account={account}
        bAccountLoaded={bAccountLoaded}
        schoolLinks={schoolLinks}
        accountLinks={subjectSiteAccountLinks(account)}
        onLessonMenuOpen={openLessonMenu}
        onSideMenuOpen={() => setBSideMenuOpen(true)}
      />
      <SubjectSiteSideMenu
        isOpen={bSideMenuOpen}
        menuLinks={menuLinks}
        account={account}
        bAccountLoaded={bAccountLoaded}
        schoolLinks={schoolLinks}
        onLessonMenuOpen={openLessonMenu}
      />
      {shell && (
        <SubjectSiteLessonMenu
          subject={shell.subject}
          activityLabel={shell.contents.Activity ?? ''}
          activitiesLabel={shell.contents.Activities ?? ''}
          isOpen={bLessonMenuOpen}
          onClose={closeAll}
        />
      )}
      <div
        className={`CoverLayer1${bCoverOpen ? ' Visible' : ''}`}
        style={bCoverShown ? { display: 'block' } : undefined}
        onClick={closeAll}
      ></div>
      <div className="MainContent">{children}</div>
      <SubjectSiteFooter
        subjectCode={subjectCode}
        links={shell?.footerLinks ?? []}
      />
    </div>
  );
};

export default SubjectSiteLayout;
